import { useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import {
  Button,
  EmptyState,
  FormGroup,
  Label,
  MultiCombobox,
  PageHeader,
  PageLayout,
  PageToolbar,
  Pagination,
  SearchInput,
  Select,
  Skeleton,
  Switch,
  Tooltip,
} from '@keboola/design';
import { Info } from '@keboola/design/icons';

import { QueryError } from '../../../components/QueryError';
import { AppCard } from '../../../features/apps/components/AppCard';
import {
  CATALOG_PAGE_SIZE,
  COMPONENT_TYPE_LABELS,
  STAGE_LABELS,
} from '../../../features/apps/constants';
import { catalogCategories, filterApps } from '../../../features/apps/helpers';
import { usePublishedApps } from '../../../features/apps/queries';
import { COMPONENT_TYPES } from '../../../features/apps/vocabulary';

/**
 * Catalog filters, kept in the URL.
 *
 * - `.catch()` on every field: a hand-edited bad value shows an unfiltered catalog, never throws.
 * - ⚠️ `.optional()`, not `.default()`: TanStack writes the parsed object back to the URL.
 *   `CATALOG_DEFAULTS` applies defaults on read, `setSearch` strips them on write.
 */
const searchSchema = z.object({
  query: z.string().optional().catch(undefined),
  type: z.enum(COMPONENT_TYPES).nullable().optional().catch(undefined),
  // Free strings: the offered list comes from `catalogCategories`, not a closed vocabulary.
  categories: z.array(z.string()).optional().catch(undefined),
  stage: z.enum(['beta', 'experimental']).nullable().optional().catch(undefined),
  keboolaOnly: z.boolean().optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
});

/** The search with defaults applied. */
type CatalogSearch = {
  query: string;
  type: (typeof COMPONENT_TYPES)[number] | null;
  categories: string[];
  stage: 'beta' | 'experimental' | null;
  keboolaOnly: boolean;
  page: number;
};

const CATALOG_DEFAULTS: CatalogSearch = {
  query: '',
  type: null,
  categories: [],
  stage: null,
  keboolaOnly: false,
  page: 1,
};

const withDefaults = (raw: Partial<CatalogSearch>): CatalogSearch => ({
  query: raw.query ?? CATALOG_DEFAULTS.query,
  type: raw.type ?? CATALOG_DEFAULTS.type,
  categories: raw.categories ?? CATALOG_DEFAULTS.categories,
  stage: raw.stage ?? CATALOG_DEFAULTS.stage,
  keboolaOnly: raw.keboolaOnly ?? CATALOG_DEFAULTS.keboolaOnly,
  page: raw.page ?? CATALOG_DEFAULTS.page,
});

/** `undefined` (absent from the URL) once back at its default. */
const orAbsent = <Value,>(value: Value, fallback: Value): Value | undefined =>
  value === fallback ? undefined : value;

/** `orAbsent` for arrays, which `===` never matches against the default. */
const orAbsentIfEmpty = (value: readonly string[]): string[] | undefined =>
  value.length === 0 ? undefined : [...value];

export const Route = createFileRoute('/_public/components/')({
  validateSearch: searchSchema,
  component: ComponentsPage,
  head: () => ({ meta: [{ title: 'Components | Keboola Developer Portal' }] }),
});

/** The `Select` value for "no filter": Radix reads `value=""` as nothing selected. */
const FILTER_ALL = 'all';

/**
 * No `other`: `filterApps` drops `other`-typed components, and `?type=other` falls to `.catch()`.
 */
const TYPE_OPTIONS = [
  { value: FILTER_ALL, label: 'All types' },
  ...COMPONENT_TYPES.map((value) => ({ value, label: COMPONENT_TYPE_LABELS[value] })),
];

const STAGE_OPTIONS = [
  { value: FILTER_ALL, label: 'All stages' },
  { value: 'beta', label: STAGE_LABELS.beta },
  { value: 'experimental', label: STAGE_LABELS.experimental },
];

/**
 * ⚠️ `Badge`'s default `gray` fails AA contrast; the edit form carries the same workaround.
 *
 * - Hoisted so the prop keeps its identity across renders.
 */
const chipColor = () => 'gray-inverse' as const;

function ComponentsPage() {
  const raw = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  // Memoised on the raw fields so the `filterApps` memo below holds.
  const search = useMemo<CatalogSearch>(
    () =>
      withDefaults({
        query: raw.query,
        type: raw.type,
        categories: raw.categories,
        stage: raw.stage,
        keboolaOnly: raw.keboolaOnly,
        page: raw.page,
      }),
    [raw.query, raw.type, raw.categories, raw.stage, raw.keboolaOnly, raw.page],
  );
  const { data: apps, isLoading, isError, error, refetch } = usePublishedApps();

  /** Any filter change resets to page 1. */
  const setSearch = (patch: Partial<CatalogSearch>) => {
    void navigate({
      // From `prev`, not `search`: queued changes stack, and foreign keys (`?brand=`) survive.
      search: (prev) => {
        const next = { ...withDefaults(prev), ...patch, page: patch.page ?? 1 };

        return {
          ...prev,
          query: orAbsent(next.query, CATALOG_DEFAULTS.query),
          type: orAbsent(next.type, CATALOG_DEFAULTS.type),
          categories: orAbsentIfEmpty(next.categories),
          stage: orAbsent(next.stage, CATALOG_DEFAULTS.stage),
          keboolaOnly: orAbsent(next.keboolaOnly, CATALOG_DEFAULTS.keboolaOnly),
          page: orAbsent(next.page, CATALOG_DEFAULTS.page),
        };
      },
      // Filter tweaks must not fill the back-button history.
      replace: true,
    });
  };

  // From the whole catalog, not `visible`, so the filter can be widened again.
  const categoryOptions = useMemo(() => catalogCategories(apps ?? []), [apps]);
  const hasCategoryOptions = categoryOptions.length > 0;

  const visible = useMemo(() => filterApps(apps ?? [], search), [apps, search]);

  const pageCount = Math.max(1, Math.ceil(visible.length / CATALOG_PAGE_SIZE));
  const page = Math.min(search.page, pageCount);
  const pageApps = visible.slice((page - 1) * CATALOG_PAGE_SIZE, page * CATALOG_PAGE_SIZE);

  const hasFilters =
    search.query !== '' ||
    search.type !== null ||
    search.categories.length > 0 ||
    search.stage !== null ||
    search.keboolaOnly;

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageHeader.Index
          title="Components"
          description="Every component published to the Keboola platform."
        />
      </PageLayout.Header>

      <PageToolbar>
        {/* `FormGroup` + `Label` keeps the row aligned with the labelled filters.
            `w-full`: `PageToolbar.Search` stretches its first child. */}
        <PageToolbar.Search>
          <FormGroup className="tw:w-full">
            <Label htmlFor="catalog-search">Search</Label>
            <SearchInput
              id="catalog-search"
              allowClear
              placeholder="Search by name, ID or description"
              value={search.query}
              onValueChange={(query) => setSearch({ query })}
            />
          </FormGroup>
        </PageToolbar.Search>

        {/* `FormGroup` + `htmlFor`: `Select`'s closed props drop an `aria-label`.
            - `flex-wrap`: the controls overflow a phone otherwise.
            - `items-end`: aligns controls under stacked labels with the bare `Switch`. */}
        <PageToolbar.Filters className="tw:flex-wrap tw:items-end tw:gap-4">
          <FormGroup className="tw:w-48">
            <Label htmlFor="catalog-type">Type</Label>
            <Select
              id="catalog-type"
              items={TYPE_OPTIONS}
              value={search.type ?? FILTER_ALL}
              onValueChange={(value) =>
                setSearch({ type: value === FILTER_ALL ? null : (value as CatalogSearch['type']) })
              }
            />
          </FormGroup>

          {/* OR within the dimension, as in kbc-ui. Options come from the catalog, since most of
              `COMPONENT_CATEGORIES` is unused. */}
          <FormGroup className="tw:w-64">
            <Label htmlFor="catalog-categories">Categories</Label>
            <MultiCombobox
              id="catalog-categories"
              allowClear
              /* Matches the `Select` triggers; argo pins `InputGroup` sizing, 2px shorter. */
              className="tw:min-h-9"
              items={categoryOptions}
              value={search.categories}
              itemToChipColor={chipColor}
              placeholder={hasCategoryOptions ? 'All categories' : 'None available'}
              /* Stays enabled while a selection exists, so a `?categories=` link can be cleared. */
              disabled={!hasCategoryOptions && search.categories.length === 0}
              onValueChange={(categories) => setSearch({ categories: [...categories] })}
            />
          </FormGroup>

          <FormGroup className="tw:w-40">
            <Label htmlFor="catalog-stage">Stage</Label>
            <Select
              id="catalog-stage"
              items={STAGE_OPTIONS}
              value={search.stage ?? FILTER_ALL}
              onValueChange={(value) =>
                setSearch({
                  stage: value === FILTER_ALL ? null : (value as CatalogSearch['stage']),
                })
              }
            />
          </FormGroup>

          {/* - `sm:ml-2`: the switch toggles rather than narrows; no inset once wrapped.
              - `h-9` matches the trigger height so midlines align; not on `Switch`, whose
                `className` lands on the pill. */}
          <div className="tw:flex tw:h-9 tw:items-center tw:gap-1.5 tw:sm:ml-2">
            <Switch
              checked={search.keboolaOnly}
              onCheckedChange={(checked) => setSearch({ keboolaOnly: checked })}
            >
              Supported by Keboola
            </Switch>

            {/* The only explanation of the filter (it means vendor type).
                - `triggerAsChild` + `Button`: the default `<span>` trigger is not keyboard-reachable.
                - Outside `Switch`: a button inside its `<label>` would toggle it. */}
            <Tooltip
              placement="top"
              tooltip="Components built and maintained by Keboola itself, rather than by a third-party vendor."
              triggerAsChild
            >
              <Button
                variant="invisible"
                size="icon-xs"
                aria-label="What “Supported by Keboola” means"
                className="tw:cursor-help"
              >
                <Info />
              </Button>
            </Tooltip>
          </div>
        </PageToolbar.Filters>
      </PageToolbar>

      {isLoading && (
        <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2 tw:lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="tw:h-44" />
          ))}
        </div>
      )}

      {/* A failed refresh keeps the stale catalog on screen. */}
      <QueryError
        isError={isError}
        hasData={apps !== undefined}
        error={error}
        onRetry={() => void refetch()}
        loadTitle="Could not load components"
        refreshTitle="Could not refresh the catalog"
        staleNote="Showing the components from the last successful load."
        titleAs="h2"
      />

      {/* Keyed on having a list, not `!isError`: a stale catalog can still filter to nothing. */}
      {!isLoading && apps !== undefined && pageApps.length === 0 && (
        <EmptyState>
          <EmptyState.Description>
            {hasFilters
              ? 'Could not find any component matching your criteria.'
              : 'No components available.'}
          </EmptyState.Description>
        </EmptyState>
      )}

      {pageApps.length > 0 && (
        <>
          <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2 tw:lg:grid-cols-3">
            {pageApps.map((app) => (
              <AppCard key={app.id} app={app} titleAs="h2" />
            ))}
          </div>

          {pageCount > 1 && (
            <Pagination className="tw:mt-8">
              <Pagination.Content>
                <Pagination.Item>
                  {/* `disabled`, not `aria-disabled`: a dead arrow leaves the tab order. */}
                  <Pagination.Previous
                    disabled={page === 1}
                    onClick={() => setSearch({ page: Math.max(1, page - 1) })}
                  />
                </Pagination.Item>
                <Pagination.Item>
                  <span className="tw:px-3 tw:text-sm tw:text-muted-foreground">
                    Page {page} of {pageCount}
                  </span>
                </Pagination.Item>
                <Pagination.Item>
                  <Pagination.Next
                    disabled={page === pageCount}
                    onClick={() => setSearch({ page: Math.min(pageCount, page + 1) })}
                  />
                </Pagination.Item>
              </Pagination.Content>
            </Pagination>
          )}
        </>
      )}
    </PageLayout>
  );
}
